import React from "react";
import type { BundleOffer } from "@/types/product";
import { useIsB2b } from "@/hooks/useIsB2b";
import { DiscountedUnitPrice } from "@/components/common/DiscountedUnitPrice/DiscountedUnitPrice";
import { isProductDiscounted } from "@/utils/discount";
import { useLocalizedTexts, t } from "@/utils/translations";
import { resolveHref } from "@/utils/url";
import { getLocale } from "@/utils/locale-store";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { fixedCmsImage } from "@/utils/cmsImage";
import styles from "./BundleOffers.module.css";

export interface BundleOffersProps {
  items: BundleOffer[];
  /** Localised "Bundle" heading from the CMS dictionary. */
  bundleLabel?: string | null;
}

/**
 * Bundle deals that include the product being viewed, shown as cards linking to
 * each bundle's own page — where the bundle is actually bought.
 *
 * Which bundles appear is a CMS decision: a bundle lists the products it should
 * be advertised on ("Items to display the bundle on"), and the backend resolves
 * that into this list. A bundle with no page link is skipped rather than rendered
 * as a dead card.
 */
export const BundleOffers: React.FC<BundleOffersProps> = ({
  items,
  bundleLabel,
}) => {
  const localizedTexts = useLocalizedTexts();
  const isB2b = useIsB2b();
  const locale = getLocale().language;

  // Resolve the href up front so a bundle with no page link is dropped rather
  // than rendered as a dead card.
  const offers = items.flatMap((offer) => {
    const link = offer.compositions?.navigation?.link;
    return link ? [{ offer, href: resolveHref(link, locale) }] : [];
  });
  if (!offers.length) return null;

  const heading =
    bundleLabel?.trim() ||
    t(localizedTexts, "Tracker.Generic.Bundle", "Bundle");

  return (
    <section className={styles.offers} data-component="bundle-offers">
      <h3 className={styles.heading}>{heading}</h3>
      <ul className={styles.list}>
        {offers.map(({ offer, href }) => {
          return (
            <li key={offer.id}>
              <a className={styles.card} href={href}>
                <div className={styles.thumbs}>
                  {offer.bundledProducts.map((product, index) => (
                    <React.Fragment key={product.id}>
                      {index > 0 && (
                        <span className={styles.plus} aria-hidden="true">
                          +
                        </span>
                      )}
                      <span className={styles.thumb}>
                        {product.image?.url && (
                          <img
                            {...fixedCmsImage(product.image.url, { width: 44 })}
                            alt={product.image.altText || product.header}
                            loading="lazy"
                          />
                        )}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
                <div className={styles.body}>
                  <span className={styles.name}>{offer.name}</span>
                  {offer.description && (
                    <span
                      className={styles.description}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(offer.description),
                      }}
                    />
                  )}
                  {/* B2B retailers see the recommended consumer price beside their net
                      price; consumers see the struck original beside the discounted
                      price when the bundle is on offer; otherwise the plain price. */}
                  {isB2b && offer.price?.formattedCalculatedPriceWithoutVat ? (
                    <DiscountedUnitPrice
                      variant="b2b"
                      standardInclVat={offer.price.formattedListPrice}
                      accentPrice={
                        offer.price.formattedCalculatedPriceWithoutVat
                      }
                      size="sm"
                      onDark
                    />
                  ) : !isB2b &&
                    isProductDiscounted(offer.price) &&
                    offer.price?.formattedListPrice &&
                    offer.price?.formattedCalculatedPrice ? (
                    <DiscountedUnitPrice
                      variant="discount"
                      standardInclVat={offer.price.formattedListPrice}
                      accentPrice={offer.price.formattedCalculatedPrice}
                      size="sm"
                      inline
                      onDark
                    />
                  ) : (
                    offer.price?.formattedCalculatedPrice && (
                      <span className={styles.price}>
                        {offer.price.formattedCalculatedPrice}
                      </span>
                    )
                  )}
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default BundleOffers;
